// Safe wrapper around `localStorage`.
//
// Browsers throw from `localStorage` under several conditions the app does
// not control:
//   * Safari Private Browsing limits storage and historically threw
//     `QuotaExceededError` on any write.
//   * Strict cookie/site-data settings can disable storage entirely
//     (`SecurityError` on read).
//   * Disk-full / corrupted profiles surface `QuotaExceededError` on write.
//
// Audit findings 1.4.4 / 1.4.5: the previous code accessed `localStorage`
// directly in module top-level scope (App.js, useTheme.js, i18n/index.js,
// api.js, Login.jsx). Any of the above conditions would throw before any
// React component mounted, and the user would see a permanent white screen.
//
// This module catches the exception, logs it once per key for debugging,
// and falls back to an in-memory `Map` so the app keeps working for the
// current tab (just without persistence).

const memoryStore = new Map();
const warned = new Set();

function warnOnce(op, key, err) {
  const tag = `${op}:${key}`;
  if (warned.has(tag)) return;
  warned.add(tag);
  // Use console directly instead of importing `lib/logger` to avoid a
  // circular dependency (logger -> sentry -> safeStorage in the future).
  if (typeof console !== "undefined" && console.warn) {
    console.warn(`[safeStorage] ${op}("${key}") failed, falling back to memory:`, err);
  }
}

function readMemory(key, fallback) {
  const mem = memoryStore.get(key);
  return mem === undefined ? fallback : mem;
}

export function getItem(key, fallback = null) {
  try {
    if (typeof localStorage === "undefined") {
      return readMemory(key, fallback);
    }
    const v = localStorage.getItem(key);
    if (v !== null) return v;
    // localStorage worked but had no entry — check the in-memory fallback in
    // case an earlier setItem failed and was diverted to memoryStore.
    return readMemory(key, fallback);
  } catch (err) {
    warnOnce("getItem", key, err);
    return readMemory(key, fallback);
  }
}

export function setItem(key, value) {
  try {
    if (typeof localStorage === "undefined") {
      memoryStore.set(key, value);
      return false;
    }
    localStorage.setItem(key, value);
    memoryStore.delete(key);
    return true;
  } catch (err) {
    warnOnce("setItem", key, err);
    memoryStore.set(key, value);
    return false;
  }
}

export function removeItem(key) {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(key);
    }
  } catch (err) {
    warnOnce("removeItem", key, err);
  }
  memoryStore.delete(key);
}

export function getJSON(key, fallback = null) {
  const raw = getItem(key, null);
  if (raw === null || raw === undefined) return fallback;
  try {
    return JSON.parse(raw);
  } catch (err) {
    warnOnce("parseJSON", key, err);
    return fallback;
  }
}

export function setJSON(key, value) {
  try {
    return setItem(key, JSON.stringify(value));
  } catch (err) {
    warnOnce("stringifyJSON", key, err);
    return false;
  }
}

const safeStorage = { getItem, setItem, removeItem, getJSON, setJSON };
export default safeStorage;
